// Per-user default-model preference (epic #635). Lets a member run their work
// on a specific model while still signing it with the ORG's provider + key —
// `provider: null` means "any provider, this model". Reads/writes
// `/v1/me/llm-pref`, scoped to the acting user within the active account.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** The resolved preference. `provider: null` = keep the org's provider, override only the model. */
export type LlmPref = {
  readonly provider: string | null;
  readonly model: string;
};

export const MY_LLM_PREF_QUERY_KEY = 'my_llm_pref';

/** The acting user's model preference (or `null` when unset). Idle with no session. */
export function useMyLlmPref(token: string | null = SESSION_TOKEN) {
  return createQuery<LlmPref | null>(() => ({
    queryKey: [MY_LLM_PREF_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () => (await apiFetch<{ pref: LlmPref | null }>('/v1/me/llm-pref', token)).pref,
  }));
}

/** Set (or replace) the preference. `provider` omitted/`null` pins the model to any provider. */
export async function setMyLlmPref(
  input: { readonly provider?: string | null; readonly model: string },
  token: string | null = SESSION_TOKEN,
): Promise<LlmPref> {
  return (
    await apiFetch<{ pref: LlmPref }>('/v1/me/llm-pref', token, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  ).pref;
}

/** Clear the preference — the member falls back to the org/pool default model (204, no body). */
export async function clearMyLlmPref(token: string | null = SESSION_TOKEN): Promise<void> {
  await apiFetch<void>('/v1/me/llm-pref', token, { method: 'DELETE' });
}
